/* eslint-disable no-console */
import * as Sentry from '@sentry/nextjs'
import { request } from 'graphql-request'
import { useCallback, useRef } from 'react'
import { useAsyncFn } from 'react-use'
import { UserToken } from '../../../types'
import { getUserQuery } from './get-user-query'

const MAX_RETRY_ATTEMPTS = 5
const NO_TOKEN_ERROR_MESSAGE = 'No tkn found'

function checkIsMoisesNetworkError(error: any): boolean {
  return !error.response && error.name === 'TypeError'
}

function checkIsFirebaseNetworkError(error: any): boolean {
  return error.code === 'auth/network-request-failed'
}

function checkIsNetworkError(error: any): boolean {
  return checkIsMoisesNetworkError(error) || checkIsFirebaseNetworkError(error)
}

function checkIsMoisesAuthError(error: any): boolean {
  return error?.response?.errors?.some(
    (err: any) => err?.extensions?.code === 'UNAUTHENTICATED'
  )
}

function waitWithExponentialDelay(attempt: number): Promise<void> {
  const delay = Math.min((attempt + 1) ** 3 * 100, 5000)
  return new Promise((resolve) => setTimeout(resolve, delay))
}

function getRetryConfig(
  error: any
): { reason: string; shouldRefresh?: boolean } | null {
  // Retry on missing token
  if (error.message === NO_TOKEN_ERROR_MESSAGE) {
    return { reason: 'missing tkn', shouldRefresh: false }
  }

  // Retry on network error (Firebase)
  if (checkIsFirebaseNetworkError(error)) {
    return { reason: 'network error' } // Refresh only if it failed while refreshing (fallback)
  }

  // Retry on network error (Moises API)
  if (checkIsMoisesNetworkError(error)) {
    return { reason: 'network error', shouldRefresh: false }
  }

  // Retry on authentication error
  if (checkIsMoisesAuthError(error)) {
    return { reason: 'auth error', shouldRefresh: true }
  }

  return null
}

export function useSafeGetUserData({
  apiUrl,
  clientHeaders,
  getValidToken,
  forceRefresh: refreshToken
}: {
  apiUrl: string
  clientHeaders: any
  getValidToken(): Promise<UserToken>
  forceRefresh?(): Promise<void>
}): ReturnType<typeof useAsyncFn<() => Promise<any>>> {
  const [state, unsafeCall] = useAsyncFn(
    async ({ attempt = 0, shouldRefresh = false } = {}): Promise<any> => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 100))
        if (shouldRefresh) {
          await refreshToken?.()
        }

        const token = await getValidToken()

        if (!token) {
          throw new Error(NO_TOKEN_ERROR_MESSAGE)
        }

        const { user } = await request(apiUrl, getUserQuery, null, {
          ...clientHeaders,
          authorization: token
        })

        if (attempt > 0) {
          console.log('GetUserData succeeded', { attempt })
        }

        return {
          user: {
            ...user,
            subscription: {
              ...user.subscription,
              active: user.subscription.isPremium
            }
          }
        }
      } catch (error: any) {
        // Exit if retry attempts has exceeded
        if (attempt >= MAX_RETRY_ATTEMPTS) {
          throw error
        }

        const retryConfig = getRetryConfig(error)

        if (!retryConfig) {
          throw error
        }

        console.log(`Retrying GetUserData due to ${retryConfig.reason}`, {
          attempt,
          shouldRefresh,
          message: error.message
        })

        if (retryConfig.shouldRefresh && !refreshToken) {
          console.warn('Refresh tkn not provided', {
            attempt,
            shouldRefresh,
            message: error.message
          })
          throw error
        }

        await waitWithExponentialDelay(attempt)
        return unsafeCall({
          attempt: attempt + 1,
          shouldRefresh: retryConfig.shouldRefresh ?? shouldRefresh
        })
      }
    },
    [apiUrl, clientHeaders, getValidToken]
  )

  const callRef = useRef<Promise<any> | null>(null)

  // Allows only one execution at a time
  const safeCall = useCallback(async () => {
    callRef.current ||= unsafeCall()
      .then((result: any) => {
        if (!(result instanceof Error)) {
          return result
        }

        const response = (result as any)?.response
        const [responseError] = response?.errors || []
        const message = responseError?.message || result.message
        const code = responseError?.extensions?.code || result.name
        const stack =
          responseError?.extensions?.stacktrace?.join('\n') || result.stack

        console.error('Failed to GetUserData:', message, { code, stack })
        Sentry.captureMessage('Failed to GetUserData', {
          level: 'error',
          extra: { error: result.stack, response }
        })

        // Detach network errors from the missing user error report
        if (checkIsNetworkError(result)) {
          throw result
        }

        return null
      })
      .finally(() => {
        callRef.current = null
      })

    return callRef.current
  }, [unsafeCall])

  return [state, safeCall]
}
